import { useParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ArrowLeft, Pencil, Trash2, Package } from "lucide-react"
import { useProduct, useDeleteProduct } from "@/hooks/use-products"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { formatDate, formatCurrency } from "@/lib/utils"
import { toast } from "sonner"

export function ProductDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useProduct(id)
  const deleteMutation = useDeleteProduct()

  const product = data?.data

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac ten produkt?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Produkt usuniety")
    navigate("/products")
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!product) {
    return <div className="text-muted-foreground">Nie znaleziono produktu</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/products")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Package size={20} className="text-primary" />
              <h1 className="page-title">
                {product.name}
              </h1>
            </div>
            {product.sku && (
              <p className="ml-7 text-sm font-mono text-muted-foreground">SKU: {product.sku}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(`/products/${id}/edit`)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input px-3 text-sm text-foreground">
            <Pencil size={14} /> {t("common.edit")}
          </button>
          <button onClick={handleDelete}
            className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm text-destructive">
            <Trash2 size={14} /> {t("common.delete")}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {/* Pricing */}
          <section className="surface-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Cennik</h2>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Cena netto</span>
                <p className="text-lg font-mono font-semibold text-foreground">
                  {product.net_price != null ? formatCurrency(product.net_price) : "\u2014"}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Stawka VAT</span>
                <p className="text-lg font-semibold text-foreground">
                  {product.vat_rate != null ? `${product.vat_rate}%` : "\u2014"}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Cena brutto</span>
                <p className="text-lg font-mono font-semibold text-primary">
                  {product.gross_price != null ? formatCurrency(product.gross_price) : "\u2014"}
                </p>
              </div>
            </div>
          </section>

          {/* Description */}
          {product.description && (
            <section className="surface-card p-4 space-y-2">
              <h2 className="text-sm font-semibold text-foreground">Opis</h2>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{product.description}</p>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="surface-card p-4 space-y-3">
            <div>
              <span className="text-xs text-muted-foreground">Status</span>
              <p>
                <Badge variant={product.is_active ? "outline" : "secondary"} className="text-xs">
                  {product.is_active ? "Aktywny" : "Nieaktywny"}
                </Badge>
              </p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Jednostka</span>
              <p className="text-sm text-foreground">{product.unit}</p>
            </div>
            {product.category && (
              <div>
                <span className="text-xs text-muted-foreground">Kategoria</span>
                <p className="text-sm text-foreground">{product.category.name}</p>
              </div>
            )}
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">Utworzono</span>
              <p className="text-sm text-foreground">{formatDate(product.created_at)}</p>
            </div>
            {product.tags.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Tagi</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {product.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
